import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, memo } from "react";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useFocusOnStaffClick } from "../../../hooks/useFocusOnStaffClick";
import { NEUTRAL, STATUS, SURFACE } from "../../../theme/tokens";
import { getStaffingDate } from "../../../utils/dateUtils";
import type { DayItem } from "../../../utils/models";
import { holidayColumnWashSx } from "../../team/Holidays/schedule/holidayTints";
import { usePageScheduleHolidays } from "../../team/Holidays/schedule/scheduleHolidaysContext";
import { DAY_WIDTH, NURSE_HIGHLIGHT_BG, NURSE_HIGHLIGHT_CLASS, toggleNurseHighlight } from "./consts";
import { NurseCellContent } from "./NurseCellContent";
import type { ComputedLocation, NurseSlot } from "./useWeeklyGapView";

export const NurseSlotRow: FC<{
	cl: ComputedLocation;
	slotIdx: number;
	weekViewData: DayItem[];
	searchedNurseId: number | null;
	onNurseClick: (nurse: NurseSlot, anchor: HTMLElement) => void;
	onNurseDoubleClick: (nurseId: number) => void;
	onMissingClick: (dayDate: DateTime, locationName: string, startMin: number, endMin: number, existingRequestId?: string) => void;
}> = memo(({ cl, slotIdx, weekViewData, searchedNurseId, onNurseClick, onNurseDoubleClick, onMissingClick }) => {
	const { scheduleHint, cancelHint } = useFocusOnStaffClick();
	const { chosenDepartment } = useHospitalData();
	const { holidayOn } = usePageScheduleHolidays();
	// A column date is already a staffing day, so only "now" (a wall-clock time) is converted. Converting the
	// column's midnight instead rolled it back a day under a morning day start and hid today's holes as past.
	const todayStaffingDay = getStaffingDate(DateTime.now(), chosenDepartment.dayStart).startOf("day");

	return (
		<Box sx={{ display: "flex", alignItems: "stretch", borderBottom: `1px solid ${NEUTRAL[100]}` }}>
			{weekViewData.map((dayItem, dayIdx) => {
				const { isBlocked, nurses, missingSlotLabels } = cl.dayData[dayIdx];
				const nurse = nurses[slotIdx];
				const missingIdx = slotIdx - nurses.length;
				const isMissing = !isBlocked && !nurse && missingIdx >= 0 && missingIdx < missingSlotLabels.length;
				const slotData = isMissing ? missingSlotLabels[missingIdx] : null;

				const isSearchMatch = nurse != null && searchedNurseId === nurse.nurseInShift.nurseId;

				return (
					<Box
						key={`nurse-slot-row-${dayItem.date.toISO()}-${dayIdx}`}
						data-nurse-id={nurse ? nurse.nurseInShift.nurseId : undefined}
						className={isSearchMatch ? NURSE_HIGHLIGHT_CLASS : undefined}
						onClick={() => {
							if (isMissing && slotData) {
								onMissingClick(dayItem.date, cl.locationName, slotData.startMin, slotData.endMin, slotData.postedRequestId ?? undefined);
								return;
							}
							if (nurse) scheduleHint();
						}}
						onDoubleClick={() => {
							cancelHint();
							if (nurse) onNurseDoubleClick(nurse.nurseInShift.nurseId);
						}}
						onMouseEnter={nurse ? () => toggleNurseHighlight(nurse.nurseInShift.nurseId, true) : undefined}
						onMouseLeave={
							nurse
								? () => {
										if (nurse.nurseInShift.nurseId !== searchedNurseId) toggleNurseHighlight(nurse.nurseInShift.nurseId, false);
									}
								: undefined
						}
						sx={(theme) => ({
							width: DAY_WIDTH,
							minWidth: DAY_WIDTH,
							px: isBlocked ? 0 : 1,
							py: isBlocked ? 0 : "2px",
							borderLeft: `1px solid ${NEUTRAL[300]}`,
							transition: "background-color 0.12s ease",
							background: isBlocked ? NEUTRAL[300] : nurse ? SURFACE : isMissing ? SURFACE : "transparent",
							...(holidayOn(dayItem.date)?.applied && holidayColumnWashSx(theme)),
							cursor: isBlocked ? "not-allowed" : isMissing || nurse ? "pointer" : "default",
							display: "flex",
							alignItems: "center",
							...(!isBlocked && { "&:hover": { bgcolor: NEUTRAL[50] } }),
							"& .nurse-menu-btn": { opacity: 0 },
							"&:hover .nurse-menu-btn": { opacity: 1 },
							[`&.${NURSE_HIGHLIGHT_CLASS}`]: { bgcolor: NURSE_HIGHLIGHT_BG },
							gap: 1,
						})}
					>
						{isBlocked ? null : nurse ? (
							<NurseCellContent nurse={nurse} onNurseClick={onNurseClick} />
						) : isMissing && slotData ? (
							slotData.postedRequestId ? (
								<Typography sx={{ fontSize: "12px", color: STATUS.SUCCESS.main, fontWeight: 600 }}>
									{slotData.postedTimeLabel ? `Posted (${slotData.postedTimeLabel})` : "Posted"}
								</Typography>
							) : dayItem.date.startOf("day") < todayStaffingDay ? null : (
								<Typography data-testid={`weekly-missing-slot-${dayIdx}-${slotIdx}`} sx={{ fontSize: "12px", color: STATUS.INFO.main, fontWeight: 500 }}>
									{slotData.timeLabel ? `Missing (${slotData.timeLabel})` : "Missing +"}
								</Typography>
							)
						) : null}
					</Box>
				);
			})}
		</Box>
	);
});
